/**
 * Player-feedback particles built on BlockParticles.chip: crit stars, enchanted-hit glyphs,
 * eating crumbs sampled from the food's own icon texels, sprint and landing dust.
 */
import type { BlockParticles } from './particles';

const rnd = (): number => Math.random() - 0.5;

/** Vanilla crit: ~ 3 * (1 + 4 * sharp) star bursts; a tan-white spray flying outwards from the target. */
export function critBurst(p: BlockParticles, x: number, y: number, z: number, h: number, count = 12): void {
  for (let i = 0; i < count; i++) {
    const k = 0.85 + Math.random() * 0.15;
    p.chip(x + rnd() * 0.5, y + Math.random() * h, z + rnd() * 0.5, rnd() * 5, 1 + Math.random() * 3, rnd() * 5,
      0.95 * k, 0.8 * k, 0.55 * k, 0.1 + Math.random() * 0.06, 0.4 + Math.random() * 0.2, 4);
  }
}

/** Enchanted hit (Sharpness, Smite, Bane): the cyan-teal variant of the crit burst. */
export function enchantHitBurst(p: BlockParticles, x: number, y: number, z: number, h: number, count = 12): void {
  for (let i = 0; i < count; i++) {
    const k = 0.8 + Math.random() * 0.2;
    p.chip(x + rnd() * 0.5, y + Math.random() * h, z + rnd() * 0.5, rnd() * 5, 1 + Math.random() * 3, rnd() * 5,
      0.35 * k, 0.9 * k, 0.9 * k, 0.1 + Math.random() * 0.06, 0.4 + Math.random() * 0.2, 4);
  }
}

/** Crumbs from the eaten item: random opaque texels of its icon, sprayed from the mouth. */
export function eatCrumbs(p: BlockParticles, icon: HTMLCanvasElement, ex: number, ey: number, ez: number,
  yaw: number, count = 5): void {
  let data: Uint8ClampedArray | null = null;
  try { data = icon.getContext('2d')?.getImageData(0, 0, icon.width, icon.height).data ?? null; } catch { /* tainted */ }
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  for (let n = 0; n < count; n++) {
    let r = 0.7, g = 0.5, b = 0.3;
    if (data) {
      for (let t = 0; t < 8; t++) {
        const o = ((Math.random() * icon.width * icon.height) | 0) * 4;
        if (data[o + 3] > 128) { r = data[o] / 255; g = data[o + 1] / 255; b = data[o + 2] / 255; break; }
      }
    }
    p.chip(ex + fx * 0.4 + rnd() * 0.15, ey - 0.25 + rnd() * 0.1, ez + fz * 0.4 + rnd() * 0.15,
      fx * 0.8 + rnd() * 1.2, 0.8 + Math.random() * 0.8, fz * 0.8 + rnd() * 1.2, r, g, b, 0.07, 0.5 + Math.random() * 0.3, 14);
  }
}

/** Landing dust count scales with the fall; none for tiny hops. */
export function landingDustCount(distance: number): number {
  return distance < 1 ? 0 : Math.min(24, 6 + Math.floor(distance * 3));
}
